<template>
  <div>
    <CoreDialog
      v-model="isDialogActiveInternal"
      data-qa="dialog_town_select"
      :size="dialogSize"
      :min-height="dialogMinHeight"
      :title="dialogTitle"
      slot-content-class="header-town__dialog-content"
    >
      <template #prependBtn>
        <v-icon
          class="cursor-pointer"
          data-qa="dialog_town_select_close"
          @click="isDialogActiveInternal = false"
        >
          ui-icon-close-not-a-circle
        </v-icon>
      </template>
      <template #default>
        <div class="d-flex flex-column flex-fill h-100 header-town__content">
          <div class="flex-shrink-0 flex-grow-0 align-self-start w-100 mb-2 header-town__search">
            <CoreSearchField
              :value="searchQuery"
              maxlength="150"
              autocomplete="off"
              :placeholder="translate('Начните вводить название, а мы подскажем')"
              data-qa="dialog_town_select_search"
              :debounce-ms="500"
              @input="searchQuery = $event"
              @input:debounced="loadAutocompleteResults"
            />
          </div>
          <div
            v-if="isSearching"
            class="flex-grow-1 overflow-y-auto py-3 mt-2 header-town__scroll"
          >
            <HeaderTownList
              :items="searchItems"
              :columns-class="townColumnsClass"
              data-qa="dialog_town_autocomplete_list"
              @select="handleTownSelect"
            />
            <div
              v-if="!searchResults.length && !isAutocompleteLoading"
              class="text-body-1 text-secondary--text pa-3 text-no-wrap header-town__autocomplete-empty"
              data-qa="dialog_town_autocomplete_empty"
            >
              {{ translate('Совпадений не найдено') }}
            </div>
          </div>
          <div
            v-else
            class="flex-grow-1 overflow-y-auto header-town__scroll"
          >
            <HeaderTownList
              :items="mainTownItems"
              :columns-class="townColumnsClass"
              wrapper-class="py-3 mt-2"
              data-qa="dialog_main_town_select_list"
              @select="handleTownSelect"
            />
            <HeaderTownList
              :items="townItems"
              :columns-class="townColumnsClass"
              wrapper-class="py-3"
              data-qa="dialog_town_select_list"
              @select="handleTownSelect"
            />
            <div class="mt-4">
              <div class="text-h6 pa-3">
                {{ translate('Другие страны') }}
              </div>
              <HeaderTownList
                :items="countryItems"
                :columns-class="townColumnsClass"
                wrapper-class="py-3 mt-2"
                data-qa="dialog_country_select_list"
                @select="handleCountrySelect"
              />
            </div>
          </div>
        </div>
      </template>
    </CoreDialog>
    <div
      v-if="!hideTrigger"
      class="cursor-pointer d-flex align-center"
      data-qa="header_town_trigger"
      @click="openDialog"
    >
      <div class="ui-icon-map-pin text-secondary--text mr-3" />
      <div class="primary--text text-body-2">
        {{ currentTownName }}
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  computed,
  ref,
  watch,
} from 'vue'
import I18NextVue from 'i18next'
import transDynamic from 'djanjucks/filters/transDynamic'
import redirect from 'src/utils/redirect'
import CoreDialog from 'src/core/CoreDialog/CoreDialog.vue'
import CoreSearchField from 'src/core/CoreSearchField/CoreSearchField.vue'
import HeaderTownList, {
  type HeaderTownListItem,
  type HeaderTownListSelectItem,
} from 'src/layouts/core-layout/components/CoreHeader/components/HeaderTownList.vue'
import useHeaderTownMenu, {
  type HeaderTownCountry,
} from 'src/layouts/core-layout/components/CoreHeader/composables/useHeaderTownMenu'
import useHeaderTownRedirect from 'src/layouts/core-layout/components/CoreHeader/composables/useHeaderTownRedirect'
import useHeaderTownSearch, {
  type HeaderTownSearchResult,
} from 'src/layouts/core-layout/components/CoreHeader/composables/useHeaderTownSearch'
import type { ContextStore } from 'src/layouts/core-layout/types'
import { useContextStore, useURLStore } from 'src/store'
import { translate } from 'src/layouts/core-layout/_functions/translate'

const props = withDefaults(defineProps<{
  value?: boolean
  hideTrigger?: boolean
}>(), {
  hideTrigger: false,
})

const emit = defineEmits<{(_event: 'input', _value: boolean): void}>()

const { patch, urls } = useURLStore()
// @ts-ignore
const { context }: ContextStore = useContextStore()
const { language } = I18NextVue
const isMobile = context.flavour === 'mobile'
const dialogSize = isMobile ? 'fullscreen' as const : 'xl' as const
const dialogMinHeight = isMobile ? undefined : 784
const townColumnsClass = isMobile ? 'column-count-1' : 'column-count-3'
const needTranslate = context.feature_flags.pd_l10n_footer_pages
const dialogTitle = computed(() => (
  `${translate('Выберите свой город в')} ${context.request.country.name_pred}`
))
const currentTownName = computed(() => transDynamic(context.town, `name,${language},${needTranslate}`))
const isDialogActiveInternal = ref(false)

const {
  countries,
  towns,
  mainTowns,
  loadTownData,
} = useHeaderTownMenu()

const { redirectToTown } = useHeaderTownRedirect()

const {
  searchQuery,
  searchResults,
  isAutocompleteLoading,
  isSearching,
  loadAutocompleteResults,
  resetSearch,
} = useHeaderTownSearch()

watch(
  () => props.value,
  value => {
    if (value !== undefined) {
      isDialogActiveInternal.value = value
    }
  },
  { immediate: true },
)

function openDialog() {
  isDialogActiveInternal.value = true
}

watch(isDialogActiveInternal, async (isActive, wasActive) => {
  if (props.value !== undefined && isActive !== props.value) {
    emit('input', isActive)
  }

  if (isActive) {
    await loadTownData()
    return
  }

  if (wasActive) {
    resetSearch()
  }
})

const mainTownItems = computed(() => mapTownItems(mainTowns.value, 'text-subtitle-1'))
const townItems = computed(() => mapTownItems(towns.value, 'text-body-1'))
const countryItems = computed(() => mapCountryItems(countries.value))
const searchItems = computed(() => searchResults.value.map(town => ({
  key: town.translit,
  name: transDynamic(town, `name,${language},${needTranslate}`),
  textClass: town.is_main_town ? 'text-subtitle-1' : 'text-body-1',
  data: town,
})))

if (process.env.IS_SERVER) {
  patch({ town: urls.town })
}

function mapTownItems(
  items: HeaderTownSearchResult[],
  textClass: string,
): HeaderTownListItem<HeaderTownSearchResult>[] {
  return items.map(town => ({
    key: town.name,
    name: transDynamic(town, `name,${language},${needTranslate}`),
    textClass,
    data: town,
  }))
}

function mapCountryItems(
  items: HeaderTownCountry[],
): HeaderTownListItem<HeaderTownCountry>[] {
  return items.map(country => ({
    key: country.name,
    name: transDynamic(country, `name,${language},${needTranslate}`),
    textClass: 'text-body-1',
    data: country,
  }))
}

function redirectToCountry(country: HeaderTownCountry) {
  redirect({ path: country.main_page_url })
}

function handleTownSelect(item: HeaderTownListSelectItem) {
  if (!('translit' in item)) {
    return
  }

  redirectToTown(item)
}

function handleCountrySelect(item: HeaderTownListSelectItem) {
  if (!('main_page_url' in item)) {
    return
  }

  redirectToCountry(item)
}
</script>

<style lang="scss">
.header-town__dialog-content {
  min-height: 0;
}
</style>

<style lang="scss" scoped>
.header-town {
  &__content,
  &__scroll {
    min-height: 0;
  }

  &__search {
    :deep(.v-input),
    :deep(.v-input__control),
    :deep(.v-input__slot) {
      flex: 0 0 auto;
      max-height: 48px;
    }
  }

  &__autocomplete-empty {
    column-span: all;
  }
}
</style>
